<template>
  <div class="question">
    <div v-if="duration && !onePageTest" class="question__duration-block">
      <div class="question__duration-block-title">{{ $tr('responses.tabs.test.answer_time') }}</div>

      <div class="question__duration-block-time">
        <vcv-progress class="question__time-progress" :value="durationPercent" :color="progressDurationColor">
          <template #default>{{ duration }} / {{ timeLimit }}</template>
        </vcv-progress>
      </div>
    </div>

    <div class="question__title">
      {{ $tr('responses.tabs.test.question') }}
      <span v-if="question.is_correct === null">({{ $tr('vacancy.tests.form.question.noAnswer') }})</span>
    </div>

    <div class="question__body" v-html="question.body"></div>

    <div class="question__answer-title">
      {{ $tr('responses.tabs.test.candidateAnswer') }}

      <span v-if="hasNoAnswers">({{ $tr('responses.tabs.test.noAnswerFromCandidate') }})</span>

      <span v-if="question.type === 'value' && question.is_correct === true" class="question__answer-title--green">
        ({{ $tr('responses.tabs.test.correct') }})
      </span>

      <span v-if="question.type === 'value' && question.is_correct === false" class="question__answer-title--red">
        ({{ $tr('responses.tabs.test.incorrect') }})
      </span>
    </div>

    <component :is="`response-${question.type}-question`" :question="question" class="question__answers" />

    <div class="question__correct">{{ answerType }}</div>
  </div>
</template>

<script>
import ResponseChoiceQuestion from './ResponseChoiceQuestion.vue';
import ResponseValueQuestion from './ResponseValueQuestion';
import ResponseFileQuestion from './ResponseFileQuestion.vue';
import VcvProgress from '@/components/VcvProgress.vue';

export default {
  name: 'Question',

  components: {
    ResponseValueQuestion,
    ResponseChoiceQuestion,
    ResponseFileQuestion,
    'response-multichoice-question': ResponseChoiceQuestion,
    VcvProgress
  },

  props: {
    question: {
      type: Object,
      default: () => {}
    },

    onePageTest: {
      type: Boolean,
      default: false
    },

    answerType: {
      type: String,
      default: ''
    }
  },

  computed: {
    duration() {
      if (!this.question.duration || this.question.time_limit === 0) {
        return false;
      }

      let duration = this.question.duration;

      if (this.question.duration > this.question.time_limit) {
        duration = this.question.time_limit;
      }

      return this.$moment.duration(duration, 'seconds').format('mm:ss', { trim: false });
    },

    durationPercent() {
      const duration = this.question.duration;
      return Math.round((duration / this.question.time_limit) * 100);
    },

    progressDurationColor() {
      if (this.durationPercent < 90) {
        return 'green';
      } else if (this.durationPercent >= 90 && this.durationPercent < 99) {
        return 'orange';
      } else {
        return 'red';
      }
    },

    timeLimit() {
      if (!this.question.time_limit) {
        return null;
      }

      return this.$moment.duration(this.question.time_limit, 'seconds').format('mm:ss', { trim: false });
    },

    hasNoAnswers() {
      if (this.question.answers.length === 1 && this.question.answers[0].body === null) {
        return true;
      }
      if (this.question.answers.every(x => !x.is_chosen)) {
        return true;
      }
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.question {
  max-width: inherit;

  &__title,
  &__duration-block-title,
  &__answer-title {
    @include mixin-text-header;
    margin-bottom: 5px;
  }

  &__answer-title--green {
    color: $green;
  }

  &__answer-title--red {
    color: $red;
  }

  &__body {
    @include mixin-text-main;
    font-size: 16px;
    margin-bottom: 25px;

    ::v-deep img {
      max-width: 100%;
      width: auto;
    }
  }

  &__time-progress {
    max-width: 250px;
  }

  &__answers {
    margin-bottom: 25px;
  }

  &__duration-block {
    margin-bottom: 25px;
  }

  &__correct {
    margin: 20px 0;
    @include mixin-text-main;
  }
}
</style>
